import React, { useState, useEffect } from 'react';
import { 
  Package, 
  Eye, 
  Edit, 
  CheckCircle, 
  XCircle,
  Truck,
  Clock,
  Search,
  Filter,
  Phone,
  MapPin,
  User,
  Mail,
  Send,
  ExternalLink,
  AlertCircle,
  RefreshCw,
  ShoppingCart
} from 'lucide-react';
import api, { getImageUrl } from '../../utils/api';
import { Order, TrackingInfo } from '../../types';
import ProductImage from '../../components/ProductImage';

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [trackingInfo, setTrackingInfo] = useState<TrackingInfo | null>(null);
  const [isCreatingShipment, setIsCreatingShipment] = useState(false);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [isBulkShipping, setIsBulkShipping] = useState(false);
  const [bulkResults, setBulkResults] = useState<any>(null);
  const [showBulkResultsModal, setShowBulkResultsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    paymentMethod: '',
    search: ''
  });

  const orderStatuses = [
    'PLACED', 'CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED'
  ];

  const statusColors = {
    PLACED: 'bg-blue-100 text-blue-800',
    CONFIRMED: 'bg-green-100 text-green-800',
    PROCESSING: 'bg-yellow-100 text-yellow-800',
    SHIPPED: 'bg-purple-100 text-purple-800',
    DELIVERED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-red-100 text-red-800'
  };

  const paymentStatusColors = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    COMPLETED: 'bg-green-100 text-green-800',
    FAILED: 'bg-red-100 text-red-800',
    REFUNDED: 'bg-gray-100 text-gray-800'
  };

  const shipmentStatusColors = {
    PENDING: 'bg-gray-100 text-gray-800',
    CREATED: 'bg-blue-100 text-blue-800',
    PICKUP_SCHEDULED: 'bg-purple-100 text-purple-800',
    PICKED_UP: 'bg-indigo-100 text-indigo-800',
    IN_TRANSIT: 'bg-yellow-100 text-yellow-800',
    DELIVERED: 'bg-green-100 text-green-800',
    RETURNED: 'bg-orange-100 text-orange-800',
    CANCELLED: 'bg-red-100 text-red-800',
    LOST: 'bg-red-200 text-red-900',
    DAMAGED: 'bg-red-200 text-red-900'
  };

  useEffect(() => {
    fetchOrders();
  }, [filters]);

  const fetchOrders = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.paymentStatus) queryParams.append('paymentStatus', filters.paymentStatus);
      if (filters.paymentMethod) queryParams.append('paymentMethod', filters.paymentMethod);
      
      const response = await api.get(`/orders?${queryParams.toString()}&limit=50`);
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderStatus = async () => {
    if (!selectedOrder) return;

    try {
      await api.put(`/orders/${selectedOrder._id}/status`, {
        status: newStatus,
        note: statusNote,
        trackingNumber: trackingNumber
      });
      
      setShowStatusModal(false);
      setSelectedOrder(null);
      setNewStatus('');
      setTrackingNumber('');
      setStatusNote('');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const createShipment = async (order: Order) => {
    if (!order) return;

    setIsCreatingShipment(true);
    try {
      const response = await api.post('/shipments/create', {
        orderId: order._id
      });
      
      if (response.data.success) {
        alert('Shipment created successfully!');
        fetchOrders();
        setShowShipmentModal(false);
      } else {
        alert('Failed to create shipment: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error creating shipment:', error);
      alert('Error creating shipment: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsCreatingShipment(false);
    }
  };

  const trackShipment = async (awbCode: string) => {
    if (!awbCode) return;

    setIsLoadingTracking(true);
    try {
      const response = await api.get(`/shipments/track/${awbCode}`);
      
      if (response.data.success) {
        setTrackingInfo(response.data.trackingInfo);
        setShowTrackingModal(true);
      } else {
        alert('Failed to get tracking info: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error tracking shipment:', error);
      alert('Error tracking shipment: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsLoadingTracking(false);
    }
  };

  const handleOrderSelection = (orderId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedOrders(prev => [...prev, orderId]);
    } else {
      setSelectedOrders(prev => prev.filter(id => id !== orderId));
    }
  };

  // Filter orders based on current filters
  const filteredOrders = orders.filter((order: Order) => {
    if (filters.status && order.orderStatus !== filters.status) return false;
    if (filters.paymentStatus && order.paymentStatus !== filters.paymentStatus) return false;
    if (filters.paymentMethod && order.paymentMethod !== filters.paymentMethod) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        order._id.toLowerCase().includes(searchLower) ||
        order.shippingAddress.name.toLowerCase().includes(searchLower) ||
        order.shippingAddress.phone.includes(filters.search)
      );
    }
    return true;
  });

  const handleSelectAll = () => {
    const eligibleOrders = filteredOrders.filter((order: Order) => 
      order.orderStatus !== 'CANCELLED' && 
      !order.shipment?.shiprocketOrderId
    );
    
    if (selectedOrders.length === eligibleOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(eligibleOrders.map((order: Order) => order._id));
    }
  };

  const createBulkShipments = async () => {
    if (selectedOrders.length === 0) {
      alert('Please select orders to create shipments for');
      return;
    }

    setIsBulkShipping(true);
    try {
      const response = await api.post('/shipments/bulk-create', {
        orderIds: selectedOrders
      });

      if (response.data.success) {
        setBulkResults(response.data.data);
        setShowBulkResultsModal(true);
        setSelectedOrders([]);
        fetchOrders();
      } else {
        alert('Failed to create bulk shipments: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error creating bulk shipments:', error);
      alert('Error creating bulk shipments: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsBulkShipping(false);
    }
  };

  const generateAWB = async (order: Order) => {
    if (!order.shipment?.shipmentId) return;

    try {
      const response = await api.post('/shipments/generate-awb', {
        shipmentId: order.shipment.shipmentId
      });
      
      if (response.data.success) {
        alert('AWB generated successfully!');
        fetchOrders();
      } else {
        alert('Failed to generate AWB: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error generating AWB:', error);
      alert('Error generating AWB: ' + (error.response?.data?.message || error.message));
    }
  };

  const openStatusModal = (order: Order) => {
    setSelectedOrder(order);
    setNewStatus(order.orderStatus);
    setTrackingNumber(order.trackingNumber || '');
    setShowStatusModal(true);
  };

  const openDetailsModal = (order: Order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PLACED':
        return <Clock className="h-4 w-4" />;
      case 'CONFIRMED':
        return <CheckCircle className="h-4 w-4" />;
      case 'PROCESSING':
        return <Package className="h-4 w-4" />;
      case 'SHIPPED':
        return <Truck className="h-4 w-4" />;
      case 'DELIVERED':
        return <CheckCircle className="h-4 w-4" />;
      case 'CANCELLED':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return <div className="p-6">Loading orders...</div>;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Order Management</h2>
          <p className="text-gray-600">Track and manage customer orders</p>
        </div>
        <div className="text-sm text-gray-500 space-y-1">
          <div>Total Orders: {orders.length}</div>
          <div className="text-orange-600 font-medium">
            COD Orders Pending Confirmation: {orders.filter(order => 
              order.paymentMethod === 'COD' && order.paymentStatus === 'PENDING'
            ).length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Statuses</option>
              {orderStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Status
            </label>
            <select
              value={filters.paymentStatus}
              onChange={(e) => setFilters({...filters, paymentStatus: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Payments</option>
              <option value="PENDING">Pending</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <select
              value={filters.paymentMethod}
              onChange={(e) => setFilters({...filters, paymentMethod: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Methods</option>
              <option value="UPI">UPI</option>
              <option value="COD">Cash on Delivery</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Orders
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Order ID, Customer..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedOrders.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <span className="text-sm text-blue-700">
              {selectedOrders.length} order{selectedOrders.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={createBulkShipments}
                disabled={isBulkShipping}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm w-full sm:w-auto"
              >
                {isBulkShipping ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Creating Shipments...
                  </>
                ) : (
                  <>
                    <Truck className="h-4 w-4" />
                    Create Bulk Shipments
                  </>
                )}
              </button>
              <button
                onClick={() => setSelectedOrders([])}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm w-full sm:w-auto"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orders Table - Desktop */}
      <div className="bg-white rounded-lg border hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <input
                    type="checkbox"
                    checked={selectedOrders.length > 0 && selectedOrders.length === filteredOrders.filter((order: Order) => 
                      order.orderStatus !== 'CANCELLED' && 
                      !order.shipment?.shiprocketOrderId
                    ).length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '200px', maxWidth: '200px' }}>
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shipment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order._id} className={`hover:bg-gray-50 ${
                  order.paymentMethod === 'COD' && order.paymentStatus === 'PENDING' 
                    ? 'bg-yellow-50 border-l-4 border-yellow-400' 
                    : order.shipment?.shipmentStatus === 'DELIVERED' 
                    ? 'bg-green-50 border-l-4 border-green-400'
                    : ''
                }`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {order.orderStatus !== 'CANCELLED' && !order.shipment?.shiprocketOrderId ? (
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order._id)}
                        onChange={(e) => handleOrderSelection(order._id, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        #{order._id.slice(-8)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </div>
                      {order.paymentMethod === 'COD' && order.paymentStatus === 'PENDING' && (
                        <div className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-full inline-block mt-1">
                          📞 Call to Confirm
                        </div>
                      )}
                      {order.shipment?.awbCode && (
                        <div className="text-xs text-blue-600 flex items-center space-x-1">
                          <Truck className="h-3 w-3" />
                          <span>AWB: {order.shipment.awbCode}</span>
                        </div>
                      )}
                      {order.shipment?.courierName && (
                        <div className="text-xs text-green-600">
                          📦 {order.shipment.courierName}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {order.shippingAddress.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.guestInfo ? order.guestInfo.email : 'Registered User'}
                    </div>
                    <div className="text-sm text-blue-600">
                      📞 {order.shippingAddress.phone || (order.guestInfo?.phone) || 'No phone'}
                    </div>
                  </td>
                  <td className="px-6 py-4" style={{ width: '200px', maxWidth: '200px' }}>
                    <div className="text-sm text-gray-900">
                      {order.items.length} item(s)
                    </div>
                    <div className="text-sm text-gray-500 space-y-1">
                      {order.items.slice(0, 2).map((item, idx) => (
                        <div 
                          key={idx} 
                          className="truncate overflow-hidden text-ellipsis whitespace-nowrap" 
                          style={{ maxWidth: '180px' }}
                          title={item.product?.name || 'Product Unavailable'}
                        >
                          {item.product?.name || 'Product Unavailable'} x{item.quantity}
                        </div>
                      ))}
                      {order.items.length > 2 && (
                        <div className="text-xs text-gray-400">
                          +{order.items.length - 2} more
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ₹{order.totalAmount.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      statusColors[order.orderStatus as keyof typeof statusColors]
                    }`}>
                      {getStatusIcon(order.orderStatus)}
                      <span className="ml-1">{order.orderStatus}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        paymentStatusColors[order.paymentStatus as keyof typeof paymentStatusColors]
                      }`}>
                        {order.paymentStatus}
                      </span>
                      <div className="text-xs text-gray-500">
                        {order.paymentMethod}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {order.shipment?.shipmentStatus ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          shipmentStatusColors[order.shipment.shipmentStatus as keyof typeof shipmentStatusColors]
                        }`}>
                          {order.shipment.shipmentStatus.replace('_', ' ')}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Not shipped</span>
                      )}
                      {order.shipment?.expectedDelivery && (
                        <div className="text-xs text-gray-500">
                          ETA: {new Date(order.shipment.expectedDelivery).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openDetailsModal(order)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openStatusModal(order)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="Update Status"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {!order.shipment?.shiprocketOrderId ? (
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowShipmentModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 p-1"
                          title="Create Shipment"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      ) : (
                        <>
                          {order.shipment.awbCode && (
                            <button
                              onClick={() => trackShipment(order.shipment!.awbCode!)}
                              className="text-purple-600 hover:text-purple-900 p-1"
                              title="Track Shipment"
                              disabled={isLoadingTracking}
                            >
                              {isLoadingTracking ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Truck className="h-4 w-4" />
                              )}
                            </button>
                          )}
                          {order.shipment.trackingUrl && (
                            <a
                              href={order.shipment.trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-orange-600 hover:text-orange-900 p-1"
                              title="Open Tracking URL"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                          {!order.shipment.awbCode && order.shipment.shipmentId && (
                            <button
                              onClick={() => generateAWB(order)}
                              className="text-yellow-600 hover:text-yellow-900 p-1"
                              title="Generate AWB"
                            >
                              <Package className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Order Cards */}
      <div className="md:hidden space-y-4">
        {/* Mobile Select All */}
        {filteredOrders.filter(order => 
          order.orderStatus !== 'CANCELLED' && 
          !order.shipment?.shiprocketOrderId
        ).length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selectedOrders.length > 0 && selectedOrders.length === filteredOrders.filter(order => 
                  order.orderStatus !== 'CANCELLED' && 
                  !order.shipment?.shiprocketOrderId
                ).length}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700 font-medium">
                {selectedOrders.length > 0 ? 'Deselect All' : 'Select All Eligible Orders'}
              </span>
            </label>
          </div>
        )}
        
        {filteredOrders.map((order) => (
          <div 
            key={order._id} 
            className={`bg-white rounded-lg border p-4 ${
              order.paymentMethod === 'COD' && order.paymentStatus === 'PENDING' 
                ? 'border-l-4 border-yellow-400 bg-yellow-50' 
                : order.shipment?.shipmentStatus === 'DELIVERED' 
                ? 'border-l-4 border-green-400 bg-green-50'
                : ''
            }`}
          >
            {/* Order Header */}
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-start gap-3">
                {/* Checkbox for bulk selection */}
                {order.orderStatus !== 'CANCELLED' && !order.shipment?.shiprocketOrderId ? (
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order._id)}
                    onChange={(e) => handleOrderSelection(order._id, e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                  />
                ) : (
                  <div className="w-4" /> // Placeholder to maintain alignment
                )}
                
                <div>
                  <h3 className="font-medium text-gray-900">#{order._id.slice(-8)}</h3>
                  <p className="text-sm text-gray-500">{new Date(order.createdAt).toLocaleDateString()}</p>
                  {order.paymentMethod === 'COD' && order.paymentStatus === 'PENDING' && (
                    <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-full inline-block mt-1">
                      📞 Call to Confirm
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-900">₹{order.totalAmount.toLocaleString()}</p>
                <p className="text-sm text-gray-500">{order.items.length} items</p>
              </div>
            </div>

            {/* Customer Info */}
            <div className="mb-3">
              <p className="font-medium text-gray-900">{order.shippingAddress.name}</p>
              <p className="text-sm text-gray-600">{order.guestInfo ? order.guestInfo.email : 'Registered User'}</p>
              <p className="text-sm text-blue-600">📞 {order.shippingAddress.phone || order.guestInfo?.phone || 'No phone'}</p>
            </div>

            {/* Status Badges */}
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                statusColors[order.orderStatus as keyof typeof statusColors]
              }`}>
                {getStatusIcon(order.orderStatus)}
                <span className="ml-1">{order.orderStatus}</span>
              </span>
              
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                paymentStatusColors[order.paymentStatus as keyof typeof paymentStatusColors]
              }`}>
                {order.paymentStatus}
              </span>
              
              {order.shipment?.shipmentStatus && (
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  shipmentStatusColors[order.shipment.shipmentStatus as keyof typeof shipmentStatusColors]
                }`}>
                  {order.shipment.shipmentStatus.replace('_', ' ')}
                </span>
              )}
            </div>

            {/* Shipment Info */}
            {order.shipment && (
              <div className="mb-3 text-sm text-gray-600">
                {order.shipment.awbCode && (
                  <div className="flex items-center">
                    <Truck className="h-4 w-4 mr-1 text-blue-600" />
                    <span>AWB: {order.shipment.awbCode}</span>
                  </div>
                )}
                {order.shipment.courierName && (
                  <div className="text-green-600">📦 {order.shipment.courierName}</div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
              <button
                onClick={() => openDetailsModal(order)}
                className="flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm"
              >
                <Eye className="h-4 w-4 mr-1" />
                Details
              </button>
              
              <button
                onClick={() => openStatusModal(order)}
                className="flex items-center px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 text-sm"
              >
                <Edit className="h-4 w-4 mr-1" />
                Update
              </button>
              
              {!order.shipment?.shiprocketOrderId ? (
                <button
                  onClick={() => {
                    setSelectedOrder(order);
                    setShowShipmentModal(true);
                  }}
                  className="flex items-center px-3 py-1.5 bg-green-100 text-green-700 rounded-md hover:bg-green-200 text-sm"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Ship
                </button>
              ) : (
                <>
                  {order.shipment.awbCode && (
                    <button
                      onClick={() => trackShipment(order.shipment!.awbCode!)}
                      className="flex items-center px-3 py-1.5 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 text-sm"
                      disabled={isLoadingTracking}
                    >
                      {isLoadingTracking ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Truck className="h-4 w-4 mr-1" />
                      )}
                      Track
                    </button>
                  )}
                  {order.shipment.trackingUrl && (
                    <a
                      href={order.shipment.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-1.5 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 text-sm"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Track
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {orders.length === 0 && !isLoading && (
          <div className="bg-white rounded-lg border p-8 text-center">
            <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Found</h3>
            <p className="text-gray-500">There are no orders matching your current filters.</p>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Update Order Status
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Status
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {orderStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tracking Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter tracking number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Note (Optional)
                  </label>
                  <textarea
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Add a note about this status change"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={updateOrderStatus}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Order Details - #{selectedOrder._id.slice(-8)}
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <User className="h-5 w-5 mr-2 text-blue-600" />
                    Customer Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-700 w-20">Name:</span>
                      <span className="text-gray-900">{selectedOrder.shippingAddress.name}</span>
                    </div>
                    {selectedOrder.guestInfo ? (
                      <>
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="font-medium text-gray-700 w-16">Email:</span>
                          <span className="text-gray-900">{selectedOrder.guestInfo.email}</span>
                        </div>
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="font-medium text-gray-700 w-16">Phone:</span>
                          <span className="text-blue-600 font-medium">
                            <a href={`tel:${selectedOrder.guestInfo.phone}`}>
                              {selectedOrder.guestInfo.phone}
                            </a>
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 bg-yellow-100 px-2 py-1 rounded">
                          Guest User
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="font-medium text-gray-700 w-16">Phone:</span>
                          <span className="text-blue-600 font-medium">
                            <a href={`tel:${selectedOrder.shippingAddress.phone}`}>
                              {selectedOrder.shippingAddress.phone}
                            </a>
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 bg-green-100 px-2 py-1 rounded">
                          Registered User
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Package className="h-5 w-5 mr-2 text-green-600" />
                    Order Summary
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Order ID:</span>
                      <span className="text-gray-900">#{selectedOrder._id.slice(-8)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Date:</span>
                      <span className="text-gray-900">
                        {new Date(selectedOrder.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Total:</span>
                      <span className="text-green-600 font-bold">₹{selectedOrder.totalAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Payment:</span>
                      <div className="text-right">
                        <div className="text-gray-900">{selectedOrder.paymentMethod}</div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          paymentStatusColors[selectedOrder.paymentStatus as keyof typeof paymentStatusColors]
                        }`}>
                          {selectedOrder.paymentStatus}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        statusColors[selectedOrder.orderStatus as keyof typeof statusColors]
                      }`}>
                        {selectedOrder.orderStatus}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                  Shipping Address
                </h4>
                <div className="text-gray-900">
                  <p className="font-medium">{selectedOrder.shippingAddress.name}</p>
                  <p>{selectedOrder.shippingAddress.street}</p>
                  <p>
                    {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state} {selectedOrder.shippingAddress.zipCode}
                  </p>
                  <p className="flex items-center mt-2">
                    <Phone className="h-4 w-4 mr-2" />
                    <a href={`tel:${selectedOrder.shippingAddress.phone}`} 
                       className="text-blue-600 hover:text-blue-800 font-medium">
                      {selectedOrder.shippingAddress.phone}
                    </a>
                  </p>
                </div>
              </div>

              {/* Shipment Information */}
              {selectedOrder.shipment && (
                <div className="mt-6 bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Truck className="h-5 w-5 mr-2 text-green-600" />
                    Shipment Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedOrder.shipment.shiprocketOrderId && (
                      <div>
                        <span className="font-medium text-gray-700">Shiprocket Order ID:</span>
                        <p className="text-gray-900">{selectedOrder.shipment.shiprocketOrderId}</p>
                      </div>
                    )}
                    {selectedOrder.shipment.awbCode && (
                      <div>
                        <span className="font-medium text-gray-700">AWB Code:</span>
                        <p className="text-blue-600 font-mono">{selectedOrder.shipment.awbCode}</p>
                      </div>
                    )}
                    {selectedOrder.shipment.courierName && (
                      <div>
                        <span className="font-medium text-gray-700">Courier:</span>
                        <p className="text-gray-900">{selectedOrder.shipment.courierName}</p>
                      </div>
                    )}
                    {selectedOrder.shipment.shipmentStatus && (
                      <div>
                        <span className="font-medium text-gray-700">Status:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                          shipmentStatusColors[selectedOrder.shipment.shipmentStatus as keyof typeof shipmentStatusColors]
                        }`}>
                          {selectedOrder.shipment.shipmentStatus.replace('_', ' ')}
                        </span>
                      </div>
                    )}
                  </div>
                  {selectedOrder.shipment.trackingUrl && (
                    <div className="mt-3">
                      <a
                        href={selectedOrder.shipment.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Track Package
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Order Items */}
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Order Items</h4>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                      <ProductImage
                        src={item.product?.images?.[0] || ''}
                        alt={item.product?.name || 'Product Unavailable'}
                        className="w-12 h-12 object-cover rounded-lg"
                        fallbackClassName="w-12 h-12 rounded-lg"
                      />
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900">
                          {item.product?.name || 'Product Unavailable'}
                        </h5>
                        <p className="text-sm text-gray-600">
                          Qty: {item.quantity} × ₹{item.price.toLocaleString()}
                        </p>
                      </div>
                      <p className="font-semibold text-gray-900">
                        ₹{(item.price * item.quantity).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call to Action for COD Orders */}
              {selectedOrder.paymentMethod === 'COD' && selectedOrder.paymentStatus === 'PENDING' && (
                <div className="mt-6 bg-orange-100 border border-orange-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-orange-900 mb-2 flex items-center">
                    <Phone className="h-5 w-5 mr-2" />
                    COD Order - Confirmation Required
                  </h4>
                  <p className="text-orange-800 mb-3">
                    This is a Cash on Delivery order. Please call the customer to confirm the order details and delivery schedule.
                  </p>
                  <div className="flex space-x-3">
                    <a
                      href={`tel:${selectedOrder.shippingAddress.phone || selectedOrder.guestInfo?.phone}`}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Call Customer
                    </a>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        openStatusModal(selectedOrder);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Update Status
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipment Creation Modal */}
      {showShipmentModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Send className="h-5 w-5 mr-2 text-green-600" />
                Create Shipment
              </h3>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Order Information</h4>
                  <div className="text-sm text-blue-800">
                    <p>Order ID: #{selectedOrder._id.slice(-8)}</p>
                    <p>Customer: {selectedOrder.shippingAddress.name}</p>
                    <p>Amount: ₹{selectedOrder.totalAmount.toLocaleString()}</p>
                    <p>Items: {selectedOrder.items.length} item(s)</p>
                  </div>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                    <p className="text-sm text-yellow-800">
                      This will create a shipment in Shiprocket. Make sure the order details are correct before proceeding.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowShipmentModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => createShipment(selectedOrder)}
                  disabled={isCreatingShipment}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isCreatingShipment ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Create Shipment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Modal */}
      {showTrackingModal && trackingInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <Truck className="h-6 w-6 mr-2 text-blue-600" />
                  Shipment Tracking
                </h3>
                <button
                  onClick={() => setShowTrackingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {/* Current Status */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="font-semibold text-blue-900 mb-2">Current Status</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-blue-800">{trackingInfo.currentStatus.status}</p>
                    <p className="text-sm text-blue-600">
                      {new Date(trackingInfo.currentStatus.statusDate).toLocaleString()}
                    </p>
                    {trackingInfo.currentStatus.location && (
                      <p className="text-sm text-blue-600 flex items-center mt-1">
                        <MapPin className="h-4 w-4 mr-1" />
                        {trackingInfo.currentStatus.location}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-700">Courier</p>
                    <p className="text-blue-600">{trackingInfo.courierName}</p>
                  </div>
                </div>
              </div>

              {/* Tracking History */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Tracking History</h4>
                <div className="space-y-4">
                  {trackingInfo.trackingHistory.map((event, index) => (
                    <div key={index} className="flex items-start space-x-3 pb-4 border-b border-gray-200 last:border-b-0">
                      <div className="flex-shrink-0">
                        <div className={`w-3 h-3 rounded-full ${
                          index === 0 ? 'bg-blue-600' : 'bg-gray-300'
                        }`}></div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className={`font-medium ${
                            index === 0 ? 'text-blue-600' : 'text-gray-900'
                          }`}>
                            {event.status}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(event.statusDate).toLocaleString()}
                          </p>
                        </div>
                        {event.message && (
                          <p className="text-sm text-gray-600 mt-1">{event.message}</p>
                        )}
                        {event.location && (
                          <p className="text-sm text-gray-500 flex items-center mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            {event.location}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowTrackingModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Results Modal */}
      {showBulkResultsModal && bulkResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <Truck className="h-6 w-6 mr-2 text-blue-600" />
                  Bulk Shipment Results
                </h3>
                <button
                  onClick={() => setShowBulkResultsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{bulkResults.summary?.total || 0}</div>
                    <div className="text-sm text-gray-600">Total</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{bulkResults.summary?.successful || 0}</div>
                    <div className="text-sm text-gray-600">Successful</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">{bulkResults.summary?.failed || 0}</div>
                    <div className="text-sm text-gray-600">Failed</div>
                  </div>
                </div>
              </div>

              {/* Successful Shipments */}
              {bulkResults.successful && bulkResults.successful.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-green-900 mb-3 flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Successful Shipments ({bulkResults.successful.length})
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {bulkResults.successful.map((item: any, index: number) => (
                      <div key={index} className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Order #{item.orderId?.slice(-8)}</span>
                          <div className="text-right">
                            {item.awbCode && <div className="text-blue-600 font-mono">{item.awbCode}</div>}
                            {item.courierName && <div className="text-gray-600">{item.courierName}</div>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Failed Shipments */}
              {bulkResults.failed && bulkResults.failed.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-red-900 mb-3 flex items-center">
                    <XCircle className="h-5 w-5 mr-2" />
                    Failed Shipments ({bulkResults.failed.length})
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {bulkResults.failed.map((item: any, index: number) => (
                      <div key={index} className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Order #{item.orderId?.slice(-8)}</span>
                          <span className="text-red-600">{item.error}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => setShowBulkResultsModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;